import { FC, useEffect, useState } from 'react'

import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'

import { Button } from 'featureSliced/entities/Button'
import { ProgressBar } from 'components/ProgressBar'
import { RenderIf } from 'components/RenderIf'

import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'

import { getProductDiscount } from 'featureSliced/shared/utils/getProductDiscount'
import {
  getPrecision,
  roundValueTo,
} from 'featureSliced/shared/utils/roundValueTo'
import { getDiscountedPrice } from 'featureSliced/shared/utils/getDiscountedPrice'
import { getItemUnits } from 'featureSliced/shared/utils/getItemUnits'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'

import {
  IMenuItemsBucket,
  ProductTemplateIdType,
} from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'

import { ReactComponent as CameraIcon } from 'assets/cameraIcon.svg'
import chevronDown from 'assets/chevronDown.svg'
import {
  ItemsContainer,
  ItemsGrid,
  ItemsGridExpander,
  MainTitle,
  Item,
  ItemImageContainer,
  ItemImage,
  ItemPrice,
} from './styled'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'

type AddMenuItemsProps = {
  product: IProduct
  productTemplateId: ProductTemplateIdType
  closeModal: () => void
}

export const AddMenuItems: FC<AddMenuItemsProps> = ({
  product,
  closeModal,
  productTemplateId,
  ...selectedKitchenInfo
}) => {
  // Hooks
  const { getProductName } = useGetTranslatedNames()
  const dispatch = useMyDispatch()
  const t = useTranslations()
  const { getProductPrice } = useGetProductPrice()

  // Actions
  const addProductToBucket = QRMBucketSlice.actions.addProductToBucket

  // Store
  const Currency = useMySelector((state) => state.app.companyData.Currency)
  const discounts = useMySelector((state) => state.specials?.discounts)

  // v2: menus come directly from the product
  const menus = product.menus || []

  // State
  const [currentSelected, setCurrentSelected] = useState(0)
  const [selectedMenuItems, setSelectedMenuItems] = useState<
    IMenuItemsBucket[]
  >([])
  const [isItemsCollapsed, setIsItemsCollapsed] = useState<boolean>(false)
  const [isCollapsedTouched, setIsCollapsedTouched] = useState<boolean>(false)

  // useEffects
  useEffect(() => {
    if (!isCollapsedTouched && menus[currentSelected]?.items?.length > 6) {
      setIsItemsCollapsed(true)
    }
  }, [menus, currentSelected])

  // Auto-add fixed items for the newly visible menu step
  useEffect(() => {
    const fixedItems =
      menus[currentSelected]?.items?.filter((item) => item.isFixed) || []
    if (!fixedItems.length) return
    setSelectedMenuItems((prev) => {
      const existingIds = new Set(
        prev
          .filter((el) => el.menuIndex === currentSelected)
          .map((el) => el.productId)
      )
      const toAdd = fixedItems.filter((item) => !existingIds.has(item.id))
      if (!toAdd.length) return prev
      return [
        ...prev,
        ...toAdd.map((item) => {
          const discountP = getProductDiscount(item as any, discounts)
          const finalPrice = item.price ?? 0
          return {
            productId: item.id,
            count: item.count ?? 1,
            menuIndex: currentSelected,
            price: finalPrice,
            name: item.title,
            kitchenInfo: item.kitchenInfo ?? [],
            imageUrl: item.imageUrl ?? undefined,
            categoryId: item.categoryId,
            isApplyDiscount: Boolean(discountP),
            isFixed: true,
            total: roundValueTo(
              getDiscountedPrice(finalPrice, discountP),
              getPrecision(Currency)
            ),
          } as unknown as IMenuItemsBucket
        }),
      ]
    })
  }, [currentSelected])

  useEffect(() => {
    const isMaxReached =
      selectedMenuItems.filter((el) => el.menuIndex === currentSelected)
        ?.length >= menus?.[currentSelected]?.max
    if (isMaxReached) {
      orderOrNext()
    }
  }, [selectedMenuItems])

  // Functions
  const addItemToSelected = (item: any, menuIndex: number) => {
    if (item.isFixed) return
    const discountP = getProductDiscount(item, discounts)
    const finalPrice = item.price ?? 0
    setSelectedMenuItems((prevValue) => [
      ...prevValue,
      {
        ...item,
        productId: item.id,
        count: 1,
        menuIndex,
        price: finalPrice,
        name: item.title,
        isApplyDiscount: Boolean(discountP),
        total: roundValueTo(
          getDiscountedPrice(finalPrice, discountP),
          getPrecision(Currency)
        ),
      },
    ])
  }

  const orderOrNext = () => {
    if (currentSelected + 1 < menus.length) {
      setCurrentSelected((prevValue) => prevValue + 1)
    }
    if (currentSelected + 1 === menus.length) {
      orderProduct()
    }
  }

  const orderProduct = () => {
    const { id, categoryId, unitSellCode } = product
    const sKI = JSON.stringify(selectedKitchenInfo)
    const sMI = selectedMenuItems
      ?.map((sMI) => sMI.productId)
      ?.sort((a, b) => a - b)
    const productToAdd = {
      uniqueId: `${productTemplateId}_${id}_${sKI}_${sMI}`,
      productId: id,
      productTemplateId,
      categoryId,
      count: 0, // 0 is needed for UPDATE to work correctly
      kitchenInfo: JSON.stringify(selectedKitchenInfo || {}),
      price: getProductPrice(product, productTemplateId).finalPrice,
      addedAsWeight: unitSellCode === 2 || unitSellCode === 3,
      menuItems: selectedMenuItems,
    }
    dispatch(addProductToBucket(productToAdd))
    closeModal()
  }

  const getSlicedArray = (arrayToSlice: any[]) => {
    if (!isCollapsedTouched && isItemsCollapsed) {
      return arrayToSlice.slice(0, 6)
    }
    return arrayToSlice
  }

  const currentMenu = menus[currentSelected]
  const minCount = currentMenu?.min || 0
  const maxCount = currentMenu?.max || 1

  return (
    <div style={{ marginTop: '35px' }}>
      <RenderIf condition={menus.length > 1}>
        <ProgressBar progress={((currentSelected + 1) / menus.length) * 100} />
      </RenderIf>
      <MainTitle>
        {minCount === maxCount
          ? t('products.buildPerfectSpecific', {
              productName: getProductName(product),
              count: minCount || 1,
            })
          : t('products.buildPerfect', {
              productName: getProductName(product),
              minCount,
              maxCount,
            })}
      </MainTitle>
      <ItemsContainer>
        <ItemsGrid>
          {getSlicedArray(currentMenu?.items || []).map((item) => {
            const isFixed = Boolean(item.isFixed)
            return (
              <Item
                key={`menu-item_${item.id}`}
                onClick={() => addItemToSelected(item, currentSelected)}
                isSelected={
                  isFixed ||
                  Boolean(
                    selectedMenuItems?.find(
                      (selectedItem) => selectedItem.productId === item.id
                    )
                  )
                }
                count={
                  isFixed
                    ? item.count ?? 1
                    : getItemUnits(item.id, selectedMenuItems)
                }
                style={
                  isFixed ? { cursor: 'default', opacity: 0.85 } : undefined
                }
              >
                <ItemImageContainer isRounded={!!item.imageUrl}>
                  <RenderIf condition={!!item.imageUrl}>
                    <ItemImage src={item.imageUrl} />
                  </RenderIf>
                  <RenderIf condition={!item.imageUrl}>
                    <CameraIcon />
                  </RenderIf>
                </ItemImageContainer>
                <ItemPrice>{item.title}</ItemPrice>
                <ItemPrice>
                  {item.price
                    ? `${convertToPrice(item.price)} ${Currency || ''}`
                    : ''}
                </ItemPrice>
              </Item>
            )
          })}
        </ItemsGrid>
        <RenderIf condition={isItemsCollapsed}>
          <ItemsGridExpander
            onClick={() => {
              setIsItemsCollapsed(false)
              setIsCollapsedTouched(true)
            }}
          >
            <img alt="Show all" src={chevronDown} />
          </ItemsGridExpander>
        </RenderIf>
      </ItemsContainer>
      <Button
        width="100%"
        buttonType="common"
        onClick={orderOrNext}
        disabled={
          selectedMenuItems.filter((el) => el.menuIndex === currentSelected)
            ?.length < minCount
        }
      >
        {currentSelected + 1 === menus.length
          ? t('buttons.accept')
          : t('common.next')}
      </Button>
    </div>
  )
}
